import Button from "../../components/ui/Button";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import "./LoginPage.css";
import {
  login as loginService,
  setupTwoFactor,
  verifyTwoFactor,
  AuthSession,
  TwoFactorChallenge,
  TwoFactorSetup,
} from "../../services/authService";
import { login as loginAction } from "../../redux/authSlice";
import { getTokenExpiration } from "../../utils/tokenUtils";
import eyeIcon from "../../assets/images/loginpageye.png";
import { useDispatch } from "react-redux";

const errorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === "string") return message;
    if (Array.isArray(message)) return message.join(" ");
    return fallback;
  }
  return error instanceof Error ? error.message : fallback;
};

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [code, setCode] = useState("");
  const [expiresAt, setExpiresAt] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/login" && !challenge) {
      navigate("/login", { replace: true });
    }
    if (location.pathname === "/login" && challenge) {
      navigate(challenge.setupRequired ? "/login/2fa/setup" : "/login/2fa", { replace: true });
    }
  }, [location.pathname, challenge, navigate]);

  useEffect(() => {
    document.title = challenge ? "Two-factor verification | Navo+ Admin" : "Sign In | Navo+ Admin";
  }, [challenge]);
  useEffect(() => {
    if (!challenge) return;
    const tick = () =>
      setSecondsLeft(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [challenge, expiresAt]);

  const completeLogin = (session: AuthSession) => {
    const exp = getTokenExpiration(session.accessToken);
    if (!exp || exp * 1000 <= Date.now())
      throw new Error("Invalid login session. Please sign in again.");
    dispatch(
      loginAction({
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
        user: session.admin,
        exp,
      }),
    );
    window.location.assign("/dashboard");
  };

  const restart = () => {
    setChallenge(null);
    setSetup(null);
    setCode("");
    setPassword("");
    setError(null);
    setExpiresAt(0);
    navigate("/login", { replace: true });
  };

  const loadSetup = async (token: string) => {
    setSetup(await setupTwoFactor(token));
  };

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await loginService({ email, password });
      if (response.requires2FA) {
        setChallenge(response);
        setPassword("");
        setSetup(null);
        setCode("");
        setExpiresAt(Date.now() + response.expiresIn * 1000);
        setSecondsLeft(response.expiresIn);
        if (response.setupRequired) await loadSetup(response.tempToken);
      }
    } catch (err) {
      setError(
        errorMessage(
          err,
          "Login failed. Please check your credentials and try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const expired = !!challenge && secondsLeft === 0;
  const handleVerify = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!challenge || Date.now() >= expiresAt) {
      setSecondsLeft(0);
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the six-digit code from your authenticator app.");
      return;
    }
    setLoading(true);
    try {
      completeLogin(
        await verifyTwoFactor(
          challenge.tempToken,
          code,
          challenge.setupRequired,
        ),
      );
    } catch (err) {
      setError(
        errorMessage(
          err,
          "Verification failed. Check your code and try again.",
        ),
      );
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-container">
      <div className="login-form-card">
        <div className="login-header">
          <p>{challenge ? challenge.admin.email : "Welcome back!"}</p>
          <h1>
            {challenge
              ? challenge.setupRequired
                ? "Set up authenticator"
                : "Two-factor verification"
              : "Login"}
          </h1>
        </div>
        {!challenge ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={passwordVisible ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
                <img
                  src={eyeIcon}
                  alt="Toggle password visibility"
                  className="password-toggle-icon"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                />
              </div>
            </div>
            {error && (
              <p className="login-error-message" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="two-factor-form">
            {expired ? (
              <p role="alert">
                Your login challenge has expired. Sign in again to continue.
              </p>
            ) : (
              <>
                {!challenge.setupRequired && (
                  <p>Enter the six-digit code from your authenticator app to finish signing in.</p>
                )}
                {challenge.setupRequired &&
                  (setup ? (
                    <div className="two-factor-setup">
                      <p>
                        Scan this QR code with Google Authenticator, Microsoft
                        Authenticator, or another authenticator app.
                      </p>
                      <img
                        src={setup.qrCodeDataUrl}
                        alt="Authenticator setup QR code"
                        className="two-factor-qr"
                      />
                      <p>Can’t scan it? Enter this setup key in your app:</p>
                      <code className="two-factor-key">{setup.manualKey}</code>
                    </div>
                  ) : (
                    <p>
                      {loading
                        ? "Loading authenticator setup..."
                        : "Authenticator setup could not be loaded."}
                    </p>
                  ))}
                {challenge.setupRequired && !setup && !loading && (
                  <Button
                    type="button"
                    className="two-factor-back"
                    onClick={async () => {
                      setLoading(true);
                      setError(null);
                      try {
                        await loadSetup(challenge.tempToken);
                      } catch (err) {
                        setError(errorMessage(err, "Unable to load setup."));
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    Retry setup
                  </Button>
                )}
                {(!challenge.setupRequired || setup) && (
                  <>
                    <div className="form-group">
                      <label htmlFor="authenticator-code">
                        Authenticator code
                      </label>
                      <input
                        id="authenticator-code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        value={code}
                        onChange={(e) =>
                          setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                        }
                        placeholder="123456"
                        required
                        disabled={loading}
                      />
                    </div>
                    <button
                      type="submit"
                      className="login-button"
                      disabled={loading || code.length !== 6}
                    >
                      {loading ? "Verifying..." : "Verify and sign in"}
                    </button>
                  </>
                )}
                <p className="two-factor-expiry">
                  Challenge expires in {Math.floor(secondsLeft / 60)}:
                  {String(secondsLeft % 60).padStart(2, "0")}
                </p>
              </>
            )}
            {error && (
              <p className="login-error-message" role="alert">
                {error}
              </p>
            )}
            <button
              type="button"
              className="two-factor-back"
              onClick={restart}
              disabled={loading}
            >
              Back to login
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
export default LoginPage;
